import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoadingButtonComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PaymentMethodInfo, RecordManualPaymentRequest } from '../../sdk/sdk';
import { ModalService } from '../../services/modal.service';
import { PaymentService } from '../../services/payment.service';

// Mirrors Groundworks.Jobs PaymentMethodConstants.ManualCheck — the one method that requires a
// check number. The authoritative selectable list is fetched from the server.
const MANUAL_CHECK_METHOD_ID = 'a1b2c3d4-0002-4000-8000-00000000000e';

@Component({
  selector: 'app-manual-post-payment-modal',
  imports: [ReactiveFormsModule, LoadingButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './manual-post-payment-modal.component.html',
})
export class ManualPostPaymentModalComponent {
  readonly activeModal = inject(NgbActiveModal);
  private readonly paymentService = inject(PaymentService);
  private readonly modalService = inject(ModalService);

  opportunityId!: string;
  isSubmitting = false;

  readonly methods = signal<PaymentMethodInfo[]>([]);

  readonly form = new FormGroup({
    paymentMethodId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    paymentDate: new FormControl<string>(new Date().toISOString().substring(0, 10), {
      nonNullable: true,
      validators: [Validators.required],
    }),
    checkNumber: new FormControl<string>('', { nonNullable: true }),
    notes: new FormControl<string>('', { nonNullable: true }),
  });

  // A plain `computed()` over `FormControl.value` would never invalidate — computed() only
  // tracks signal reads, and a FormControl's value is not a signal, so the derived value would
  // be calculated once and cached forever. Instead this is a signal explicitly set whenever the
  // control's value changes, which also keeps OnPush change detection correctly informed.
  readonly isCheck = signal(false);

  constructor() {
    this.paymentService
      .getManualPaymentMethods()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (methods) => this.methods.set(methods),
        error: (err: { status?: number; response?: string; detail?: string }) => {
          // Without methods there is nothing selectable, so the modal cannot function — close it
          // and surface why rather than leaving an empty, unexplained dropdown.
          this.activeModal.dismiss('methods-load-error');
          this.modalService.openErrorModal('Manual payment failed', this.errorMessage(err));
        },
      });

    this.form.controls.paymentMethodId.valueChanges.pipe(takeUntilDestroyed()).subscribe((id) => {
      this.isCheck.set(id?.toLowerCase() === MANUAL_CHECK_METHOD_ID);
      this.applyCheckValidators(id);
    });
  }

  private applyCheckValidators(methodId: string): void {
    const checkNumber = this.form.controls.checkNumber;
    if (methodId?.toLowerCase() === MANUAL_CHECK_METHOD_ID) {
      checkNumber.addValidators(Validators.required);
    } else {
      checkNumber.clearValidators();
      checkNumber.setValue('');
    }
    checkNumber.updateValueAndValidity();
  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const value = this.form.getRawValue();
    const request = new RecordManualPaymentRequest({
      opportunityId: this.opportunityId,
      amount: value.amount!,
      paymentDate: new Date(value.paymentDate),
      paymentMethodId: value.paymentMethodId,
      checkNumber: this.isCheck() && value.checkNumber ? value.checkNumber : undefined,
      notes: value.notes ? value.notes : undefined,
    });

    this.paymentService.recordManualPayment(request).subscribe({
      next: (result) => {
        this.isSubmitting = false;
        this.activeModal.close(result);
      },
      error: (err: { status?: number; response?: string; detail?: string }) => {
        this.isSubmitting = false;
        this.activeModal.dismiss('error');
        this.modalService.openErrorModal('Manual payment failed', this.errorMessage(err));
      },
    });
  }

  private errorMessage(err: { status?: number; response?: string; detail?: string }): string {
    if (err?.status === 503 || err?.status === 502) {
      return 'Payments are temporarily unavailable. Please try again later.';
    }
    // A declared problem status (e.g. 422) makes the generated SDK throw the parsed ProblemDetails
    // object (carrying `.detail`); an undeclared status throws a SwaggerException carrying the raw
    // `.response` body. Prefer the object's detail, then fall back to parsing the raw body.
    const detail = err?.detail ?? this.parseDetail(err?.response);
    if (detail) {
      return detail;
    }
    return 'Something went wrong recording the payment. Please try again.';
  }

  private parseDetail(response?: string): string | undefined {
    if (!response) {
      return undefined;
    }
    try {
      const body = JSON.parse(response) as { detail?: string; errorCode?: string };
      return body.detail;
    } catch {
      return undefined;
    }
  }
}
